import { useEffect, useMemo, useState } from "react";
import confetti from "canvas-confetti";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { useFxRates } from "../../lib/queries/useFxRates";
import { Topbar } from "../../components/AppShell";
import { LoadingPanel } from "../../components/LoadingPanel";
import {
  STRIPE_MRR_HISTORICAL_SQL,
  STRIPE_MRR_CURRENT_SQL,
  PRODUCT_SPLIT_SQL,
  STRIPE_PER_SUB_DELTA_SQL,
  buildNetSeatsByCcySql,
  mapStripeMrrRows,
  mapCurrentMrrRows,
  mapProductSplitRows,
  mapPerSubRows,
  mapNetSeatsByCcyRows,
} from "../MrrDashboard/sql";
import type { RegionKey, DashboardData, NetSeatsByCcyRow } from "../MrrDashboard/sql";
import {
  REGIONS,
  RNAMES,
  COLORS,
  CCY,
  MONTHS,
  MONTH_YMS,
  CURRENT_MONTH_IDX,
  idxToYearMonth,
  fmt,
  buildData,
} from "../MrrDashboard/helpers";
import { buildFxMaps, buildFxMonthCloseMaps, fxFromDate, FX_BASE, FX_QUOTES } from "../MrrDashboard/fx";
import type { FxArrMap } from "../MrrDashboard/fx";

const DB = { database: "stripe" };

function formatError(err: unknown): string {
  if (err == null) return "unknown error";
  if (err instanceof Error) return err.message || err.toString();
  if (typeof err === "string") return err;
  try { return JSON.stringify(err); } catch { return String(err); }
}

function renderNet(n: number) {
  if (n === 0) return <span style={{ color: "var(--fg-3)", fontWeight: 500 }}>0</span>;
  if (n > 0) return <span style={{ color: "var(--color-jade)" }}>+{n.toLocaleString()}</span>;
  return <span style={{ color: "var(--fg-danger)" }}>{n.toLocaleString()}</span>;
}

function SeatStat({ value, color }: { label: string; value: number; color: string }) {
  const muted = value === 0;
  const valueColor = muted
    ? "var(--fg-3)"
    : value < 0
      ? "var(--fg-danger)"
      : "var(--fg-1)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <span
        style={{
          fontSize: 16,
          fontWeight: 700,
          fontFamily: "var(--font-mono)",
          color: valueColor,
        }}
      >
        {muted ? "0" : value > 0 ? `+${value}` : value}
      </span>
    </div>
  );
}

// Manual seat overrides for invoiced firms whose seats aren't in Stripe and
// haven't yet been onboarded into the app DB. Each entry contributes its
// seats to the Net Seats column for the month identified by startYm. Drop
// the entry once the firm exists in app DB / Stripe.
type ManualInvoicedSeatOverride = {
  company: string;
  region: RegionKey;
  plan: "pro" | "plus";
  seats: number;
  startYm: string; // YYYY-MM the firm first contributes
};

const MANUAL_INVOICED_SEAT_OVERRIDES: ManualInvoicedSeatOverride[] = [
  { company: "Radcliff Newlands", region: "UK", plan: "pro", seats: 15, startYm: "2026-05" },
];

export function MrrSandbox() {
  const [showUSD, setShowUSD] = useState(true);
  const mi = CURRENT_MONTH_IDX;

  const histResult = useAdminQuery(STRIPE_MRR_HISTORICAL_SQL, [], DB);
  const curResult = useAdminQuery(STRIPE_MRR_CURRENT_SQL, [], DB);
  const splitResult = useAdminQuery(PRODUCT_SPLIT_SQL, [], DB);
  const perSubResult = useAdminQuery(STRIPE_PER_SUB_DELTA_SQL, [], DB);
  const monthStartIso = `${idxToYearMonth(mi)}-01`;
  // Net seat change per region per plan, derived from Stripe sub state.
  // Captures new subs, expansions, contractions, and cancellations in one shot.
  const netSeatsResult = useAdminQuery(buildNetSeatsByCcySql(monthStartIso), [], DB);
  const fxResult = useFxRates(fxFromDate(MONTH_YMS), FX_BASE, [...FX_QUOTES]);

  type PlanSplit = { pro: number; plus: number };
  const netSeatsByRegion = useMemo<Record<RegionKey, PlanSplit>>(() => {
    const out: Record<RegionKey, PlanSplit> = {
      NZ: { pro: 0, plus: 0 },
      AU: { pro: 0, plus: 0 },
      UK: { pro: 0, plus: 0 },
      Other: { pro: 0, plus: 0 },
    };
    if (!netSeatsResult.data) return out;
    const rows: NetSeatsByCcyRow[] = mapNetSeatsByCcyRows(
      netSeatsResult.data.columns,
      netSeatsResult.data.rows as unknown[][],
    );
    const ccyToRegion: Record<string, RegionKey> = { NZD: "NZ", AUD: "AU", GBP: "UK" };
    for (const row of rows) {
      const region = ccyToRegion[row.ccy] ?? "Other";
      const plan = row.plan.toLowerCase();
      if (plan === "pro") out[region].pro += row.net_seats;
      else if (plan === "plus") out[region].plus += row.net_seats;
    }
    // Apply manual overrides for firms billed via invoice that aren't yet in
    // Stripe (and so not captured by the Stripe-derived net seats query).
    const selectedYm = monthStartIso.slice(0, 7);
    for (const ovr of MANUAL_INVOICED_SEAT_OVERRIDES) {
      if (ovr.startYm === selectedYm) {
        out[ovr.region][ovr.plan] += ovr.seats;
      }
    }
    return out;
  }, [netSeatsResult.data, monthStartIso]);

  const isLoading =
    histResult.isLoading ||
    curResult.isLoading ||
    splitResult.isLoading ||
    perSubResult.isLoading ||
    fxResult.isLoading;
  const isError =
    histResult.isError ||
    curResult.isError ||
    splitResult.isError ||
    perSubResult.isError ||
    fxResult.isError;

  const D: DashboardData | null = useMemo(() => {
    if (
      !histResult.data ||
      !curResult.data ||
      !splitResult.data ||
      !perSubResult.data ||
      !fxResult.data
    ) {
      return null;
    }
    const historical = mapStripeMrrRows(histResult.data.columns, histResult.data.rows as unknown[][]);
    const current = mapCurrentMrrRows(curResult.data.columns, curResult.data.rows as unknown[][]);
    const productSplit = mapProductSplitRows(splitResult.data.columns, splitResult.data.rows as unknown[][]);
    const perSub = mapPerSubRows(perSubResult.data.columns, perSubResult.data.rows as unknown[][]);
    // Key by (ccy, region) so e.g. an IE GBP new sub is subtracted from the
    // GBP/Other current row, not GBP/UK.
    const newSubsByKey: Record<string, number> = {};
    const k = (ccy: string, region: string): string => `${ccy}|${region}`;
    for (const row of perSub) {
      if (row.prior_mrr === 0 && row.current_mrr > 0) {
        const key = k(row.ccy, row.region);
        newSubsByKey[key] = (newSubsByKey[key] ?? 0) + row.current_mrr;
      }
    }
    const priorCommitment = current.map((r) => ({
      ccy: r.ccy,
      region: r.region,
      mrr_local: r.mrr_local - (newSubsByKey[k(r.ccy, r.region)] ?? 0),
    }));
    const { fxUsd, fxNzd } = buildFxMaps(fxResult.data.daily, MONTH_YMS);
    return buildData(historical, current, productSplit, fxUsd, fxNzd, priorCommitment);
  }, [histResult.data, curResult.data, splitResult.data, perSubResult.data, fxResult.data]);

  // Celebrate when the business EoM target is hit. Fires once per (target,
  // month) per browser; refresh after the first cheer is a no-op. Must live
  // above the !D early return so hooks order stays stable across renders.
  useEffect(() => {
    if (!D || !fxResult.data) return;
    const cm = CURRENT_MONTH_IDX;
    const { fxUsdClose, fxUsdPriorClose } = buildFxMonthCloseMaps(fxResult.data.daily, MONTH_YMS);
    const currUSD = REGIONS.reduce(
      (s, r) => s + (D.mrr[r]?.[cm] ?? 0) * (fxUsdClose[r][cm] ?? 1),
      0,
    );
    const goalUSDLocal = REGIONS.reduce(
      (s, r) => s + (D.tgt[r]?.[cm] ?? 0) * (fxUsdPriorClose[r][cm] ?? 1),
      0,
    );
    if (currUSD < goalUSDLocal) return;
    const key = `mrr-celebrated-business-${idxToYearMonth(cm)}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    const duration = 2800;
    const end = Date.now() + duration;
    const colors = ["#2F7A8A", "#7c3aed", "#059669", "#d97706", "#DC6A4C"];
    (function frame() {
      confetti({
        particleCount: 6,
        angle: 60,
        spread: 65,
        startVelocity: 65,
        origin: { x: 0, y: 0.85 },
        colors,
      });
      confetti({
        particleCount: 6,
        angle: 120,
        spread: 65,
        startVelocity: 65,
        origin: { x: 1, y: 0.85 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, [D, fxResult.data]);

  if (!D) {
    return (
      <>
        <Topbar crumbs={["Dashboards", "MRR Tracking"]} />
        <div className="page">
          <div className="page-header">
            <h1>MRR Tracking</h1>
          </div>
          {isLoading && <LoadingPanel />}
          {isError && (
            <pre style={{ color: "var(--fg-danger)", whiteSpace: "pre-wrap", fontSize: 12 }}>
              Failed to load data.{"\n"}
              {histResult.isError && `historical: ${formatError(histResult.error)}\n`}
              {curResult.isError && `current: ${formatError(curResult.error)}\n`}
              {splitResult.isError && `split: ${formatError(splitResult.error)}\n`}
              {perSubResult.isError && `per-sub: ${formatError(perSubResult.error)}\n`}
              {fxResult.isError && `fx: ${formatError(fxResult.error)}\n`}
            </pre>
          )}
        </div>
      </>
    );
  }

  // Per-month close FX (each month at its own end-of-month rate; current month uses
  // today's rate). Drives both actuals and locked-prior-close targets so intra-month
  // FX drift shows as a headwind / tailwind. fxResult.data is guaranteed non-null
  // here because D's useMemo depends on it.
  const { fxUsdClose, fxUsdPriorClose } = buildFxMonthCloseMaps(
    fxResult.data!.daily,
    MONTH_YMS,
  );
  const fxU = (r: RegionKey, i: number): number =>
    (fxUsdClose as FxArrMap)[r]?.[i] ?? D.fxUsd[r][CURRENT_MONTH_IDX] ?? 1;
  const fxTgt = (r: RegionKey, i: number): number =>
    (fxUsdPriorClose as FxArrMap)[r]?.[i] ?? fxU(r, i);
  const g = (r: RegionKey, i: number) => {
    const v = D.mrr[r]?.[i] ?? 0;
    return showUSD ? v * fxU(r, i) : v;
  };
  const gtEOM = (r: RegionKey, i: number) => {
    const v = D.tgt[r]?.[i] ?? 0;
    return showUSD ? v * fxTgt(r, i) : v;
  };

  const today = new Date();
  const dayOfMonth = today.getDate();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const monthFraction = dayOfMonth / daysInMonth;
  const daysRemaining = Math.max(0, daysInMonth - dayOfMonth);

  const isCurrentMonth = mi === CURRENT_MONTH_IDX;
  const sumUsdAt = (i: number) =>
    REGIONS.reduce((s, r) => s + (D.mrr[r]?.[i] ?? 0) * fxU(r, i), 0);
  const sumTgtUsdAt = (i: number) =>
    REGIONS.reduce((s, r) => s + (D.tgt[r]?.[i] ?? 0) * fxTgt(r, i), 0);

  const currentUSD = Math.round(sumUsdAt(mi));
  const goalUSD = Math.round(sumTgtUsdAt(mi));
  const requiredUSD = goalUSD - currentUSD;

  const priorUSDAtPriorClose = mi > 0
    ? REGIONS.reduce(
        (s, r) => s + (D.mrr[r]?.[mi - 1] ?? 0) * fxTgt(r, mi),
        0,
      )
    : 0;
  const monthlyGrowthPct = priorUSDAtPriorClose > 0
    ? ((currentUSD - priorUSDAtPriorClose) / priorUSDAtPriorClose) * 100
    : 0;

  // Linear-extrapolation projection: if the rate of growth captured so far holds for
  // the rest of the month, what will EoM look like? For closed months, projection = actual.
  const projectedEoMUSD =
    isCurrentMonth && monthFraction > 0
      ? Math.round(priorUSDAtPriorClose + (currentUSD - priorUSDAtPriorClose) / monthFraction)
      : currentUSD;
  const projectedHitsTarget = projectedEoMUSD >= goalUSD;
  // Pace variance as of today: captured growth minus the linearly-expected
  // captured growth at this day of the month. Positive = ahead of pace today,
  // negative = behind pace today.
  const requiredGrowthUSD = goalUSD - priorUSDAtPriorClose;
  const capturedGrowthUSD = currentUSD - priorUSDAtPriorClose;
  const expectedGrowthSoFarUSD = isCurrentMonth
    ? requiredGrowthUSD * monthFraction
    : requiredGrowthUSD;
  const paceVarianceUSD = Math.round(capturedGrowthUSD - expectedGrowthSoFarUSD);
  const PACE_ON_TRACK_USD = 100;
  const perDayRequired = daysRemaining > 0 && requiredUSD > 0
    ? Math.round(requiredUSD / daysRemaining)
    : 0;
  const hasPriorMonth = mi > 0;

  // Last-updated timestamp for the footer.
  const lastUpdatedAt = Math.max(
    histResult.dataUpdatedAt || 0,
    curResult.dataUpdatedAt || 0,
    splitResult.dataUpdatedAt || 0,
    perSubResult.dataUpdatedAt || 0,
    netSeatsResult.dataUpdatedAt || 0,
    fxResult.dataUpdatedAt || 0,
  );
  const minsAgo = Math.floor((Date.now() - lastUpdatedAt) / 60000);
  const updatedText =
    minsAgo <= 0 ? "just now" : `${minsAgo} min${minsAgo === 1 ? "" : "s"} ago`;

  return (
    <>
      <Topbar
        crumbs={["Dashboards", "MRR Tracking"]}
        right={
          <div className="btn-group">
            <button
              type="button"
              className={`btn btn-sm ${!showUSD ? "is-active" : ""}`}
              onClick={() => setShowUSD(false)}
            >
              Local CCY
            </button>
            <button
              type="button"
              className={`btn btn-sm ${showUSD ? "is-active" : ""}`}
              onClick={() => setShowUSD(true)}
            >
              USD
            </button>
          </div>
        }
      />
      <div className="page">
        <div className="page-header">
          <div>
            <h1>MRR Tracking</h1>
            <p className="lede">
              How we are tracking against the monthly MRR target.
            </p>
          </div>
        </div>

        {/* Hero KPIs */}
        <div className="mrr-kpi-grid">
          {/* Current MRR */}
          <div className="kpi">
            <div className="kpi-label">{isCurrentMonth ? "Current MRR" : `${MONTHS[mi]} MRR`}</div>
            <div className="kpi-value" style={{ fontFamily: "var(--font-mono)" }}>
              US${fmt(currentUSD)}
            </div>
            <div className="kpi-sub">
              {hasPriorMonth ? (
                <span
                  className="badge"
                  style={{
                    background: monthlyGrowthPct >= 0 ? "var(--color-jade-soft)" : "var(--color-coral-soft)",
                    color: monthlyGrowthPct >= 0 ? "var(--color-jade)" : "var(--fg-danger)",
                  }}
                >
                  {monthlyGrowthPct >= 0 ? "+" : ""}{monthlyGrowthPct.toFixed(1)}% MoM
                </span>
              ) : (
                <span className="badge b-gray">First tracked month</span>
              )}
            </div>
          </div>

          {/* EoM Target */}
          <div className="kpi">
            <div className="kpi-label">
              {isCurrentMonth ? "EoM target" : `${MONTHS[mi]} Target`}
            </div>
            <div className="kpi-value" style={{ fontFamily: "var(--font-mono)" }}>
              US${fmt(goalUSD)}
            </div>
            <div className="kpi-sub" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {isCurrentMonth ? (
                requiredUSD > 0 ? (
                  <>
                    <span className="badge b-gray">${fmt(requiredUSD)} to go</span>
                    {perDayRequired > 0 && (
                      <span className="badge b-gray">${fmt(perDayRequired)}/day needed</span>
                    )}
                  </>
                ) : (
                  <span
                    className="badge"
                    style={{ background: "var(--color-jade-soft)", color: "var(--color-jade)" }}
                  >
                    Target hit
                  </span>
                )
              ) : (
                <span
                  className="badge"
                  style={{
                    background: requiredUSD <= 0 ? "var(--color-jade-soft)" : "var(--color-coral-soft)",
                    color: requiredUSD <= 0 ? "var(--color-jade)" : "var(--fg-danger)",
                  }}
                >
                  {requiredUSD <= 0
                    ? `Hit by $${fmt(Math.abs(requiredUSD))}`
                    : `Missed by $${fmt(requiredUSD)}`}
                </span>
              )}
            </div>
          </div>

          {/* Projected EoM (highlighted) */}
          <div
            className="kpi"
            style={{
              border: "2px solid var(--primary-5)",
              boxShadow: "0 0 0 4px rgba(47,122,138,0.06)",
            }}
          >
            <div className="kpi-label">
              {isCurrentMonth ? (
                <>Projected EoM <span style={{ color: "var(--fg-3)", fontWeight: 400 }}>· at current pace</span></>
              ) : (
                <>EoM result</>
              )}
            </div>
            <div
              className="kpi-value"
              style={{
                color: projectedHitsTarget ? "var(--color-jade)" : "var(--fg-danger)",
              }}
            >
              {isCurrentMonth
                ? (projectedHitsTarget ? "On pace" : "Behind")
                : (projectedHitsTarget ? "Hit" : "Missed")}
            </div>
            <div className="kpi-sub">
              {hasPriorMonth && (
                Math.abs(paceVarianceUSD) < PACE_ON_TRACK_USD ? (
                  <span
                    className="badge"
                    style={{ background: "var(--color-jade-soft)", color: "var(--color-jade)" }}
                  >
                    {isCurrentMonth ? "On pace" : "On target"}
                  </span>
                ) : (
                  <span
                    className="badge"
                    style={{
                      background: paceVarianceUSD > 0 ? "var(--color-jade-soft)" : "var(--color-coral-soft)",
                      color: paceVarianceUSD > 0 ? "var(--color-jade)" : "var(--fg-danger)",
                    }}
                  >
                    {isCurrentMonth
                      ? (paceVarianceUSD > 0
                          ? `$${fmt(Math.abs(paceVarianceUSD))} ahead of pace`
                          : `$${fmt(Math.abs(paceVarianceUSD))} behind pace`)
                      : (paceVarianceUSD > 0
                          ? `Hit by $${fmt(Math.abs(paceVarianceUSD))}`
                          : `Missed by $${fmt(Math.abs(paceVarianceUSD))}`)}
                  </span>
                )
              )}
            </div>
          </div>
        </div>

        {/* Single region progress panel */}
        <RegionProgress
          D={D}
          mi={mi}
          showUSD={showUSD}
          monthFraction={monthFraction}
          g={g}
          gtEOM={gtEOM}
          fxTgt={fxTgt}
          netSeatsByRegion={netSeatsByRegion}
          updatedText={updatedText}
        />
      </div>
    </>
  );
}

function RegionProgress({
  D,
  mi,
  showUSD,
  monthFraction,
  g,
  gtEOM,
  fxTgt,
  netSeatsByRegion,
  updatedText,
}: {
  D: DashboardData;
  mi: number;
  showUSD: boolean;
  monthFraction: number;
  g: (r: RegionKey, i: number) => number;
  gtEOM: (r: RegionKey, i: number) => number;
  fxTgt: (r: RegionKey, i: number) => number;
  netSeatsByRegion: Record<RegionKey, { pro: number; plus: number }>;
  updatedText: string;
}) {
  const isCurrentMonth = mi === CURRENT_MONTH_IDX;

  type RegionRow = {
    r: RegionKey;
    name: string;
    sym: string;
    actual: number;
    target: number;
    start: number;
    captured: number;
    needed: number;
    capturedFrac: number;
    pacePct: number;
    onTrack: boolean;
    projected: number;
    momPct: number | null;
    netTotal: number;
    pro: number;
    plus: number;
  };

  const rows: RegionRow[] = REGIONS.map((r) => {
    const actual = g(r, mi) || 0;
    const target = gtEOM(r, mi) || 0;
    const startLocal =
      isCurrentMonth && D.monthStart[r] != null
        ? D.monthStart[r]
        : D.mrr[r]?.[mi - 1] || 0;
    const start = showUSD ? startLocal * fxTgt(r, mi) : startLocal;
    const captured = actual - start;
    const needed = target - start;
    const capturedFrac = needed > 0 ? captured / needed : actual >= target ? 1 : 0;
    const pacePct = isCurrentMonth && monthFraction > 0
      ? (capturedFrac / monthFraction) * 100
      : capturedFrac * 100;
    const onTrack = isCurrentMonth ? capturedFrac >= monthFraction : capturedFrac >= 1;
    const projected = isCurrentMonth && monthFraction > 0
      ? start + captured / monthFraction
      : actual;
    // MoM in the selected currency, anchored on the same locked prior-close base
    // as the headline so they tell the same story (FX drift = headwind / tailwind).
    const momPct = start > 0 ? ((actual - start) / start) * 100 : null;
    const seats = netSeatsByRegion[r];
    return {
      r,
      name: r === "Other" ? "International" : RNAMES[r],
      sym: showUSD ? "$" : CCY[r],
      actual,
      target,
      start,
      captured,
      needed,
      capturedFrac,
      pacePct,
      onTrack,
      projected,
      momPct,
      netTotal: seats.pro + seats.plus,
      pro: seats.pro,
      plus: seats.plus,
    };
  });

  const sorted = [...rows].sort((a, b) => b.actual - a.actual);

  return (
    <div className="card">
      <div className="card-h" style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
        <h2>Tracking by region</h2>
        <span className="sub">
          Growth captured = MRR added this month / MRR needed to hit target.
          {isCurrentMonth ? ` The vertical marker shows month elapsed (${Math.round(monthFraction * 100)}%).` : ""}
          {" "}Sorted by current MRR.
        </span>
      </div>
      <div className="card-body" style={{ display: "flex", flexDirection: "column" }}>
        {/* Column headers — hidden on narrow screens via .mrr-region-head media query */}
        <div className="mrr-region-head">
          <div>Region</div>
          <div>Progress</div>
          <div>MoM</div>
          <div>Pro</div>
          <div>Plus</div>
          <div>Status</div>
        </div>
        {sorted.map((row) => (
          <RegionRow
            key={row.r}
            row={row}
            isCurrentMonth={isCurrentMonth}
            monthFraction={monthFraction}
          />
        ))}
        <div
          style={{
            paddingTop: 12,
            marginTop: 8,
            borderTop: "1px solid var(--border-default)",
            fontSize: 11,
            color: "var(--fg-3)",
            textAlign: "right",
          }}
        >
          Updated {updatedText}
        </div>
      </div>
    </div>
  );
}

function RegionRow({
  row,
  isCurrentMonth,
  monthFraction,
}: {
  row: {
    r: RegionKey;
    name: string;
    sym: string;
    actual: number;
    target: number;
    start: number;
    captured: number;
    needed: number;
    capturedFrac: number;
    pacePct: number;
    onTrack: boolean;
    projected: number;
    momPct: number | null;
    netTotal: number;
    pro: number;
    plus: number;
  };
  isCurrentMonth: boolean;
  monthFraction: number;
}) {
  const capturedPct = Math.max(0, Math.min(row.capturedFrac * 100, 100));
  // Three states: Ahead (>= 105% pace), On track (95-105%), Behind (< 95%).
  const onTrackBand = row.pacePct >= 95;
  const ahead = row.pacePct >= 105;
  const positive = onTrackBand;
  const fillColor = positive ? "var(--color-jade)" : "var(--fg-danger)";
  const fillBg = positive ? "var(--color-jade-soft)" : "var(--color-coral-soft)";
  const badgeBg = positive ? "var(--color-jade-soft)" : "var(--color-coral-soft)";
  const badgeFg = positive ? "var(--color-jade)" : "var(--fg-danger)";
  const paceText = isCurrentMonth
    ? (ahead ? "Ahead" : onTrackBand ? "On track" : "Behind")
    : (positive ? "Hit" : "Missed");

  return (
    <div className="mrr-region-row">
      {/* Region column: name + value + target */}
      <div className="mrr-region-info">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: COLORS[row.r], flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: 14 }}>{row.name}</span>
          {row.capturedFrac >= 1 && (
            <span
              className="mrr-target-hit-mark"
              aria-label={`${row.name} target hit`}
              title={`${row.name} EoM target hit`}
              style={{ fontSize: 14 }}
            >
              🎯
            </span>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              fontFamily: "var(--font-mono)",
              letterSpacing: "-0.02em",
            }}
          >
            {row.sym}{fmt(row.actual)}
          </span>
          <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
            target {row.sym}{fmt(row.target)}
          </span>
        </div>
      </div>
      {/* Progress bar — fills available width, with month-elapsed marker. */}
      <div className="mrr-progress-cell" style={{ position: "relative", height: 22 }}>
            {/* Solid-black portion of the month-elapsed marker — renders
                BEFORE the bar so the bar covers the middle, leaving the
                overhanging tabs above and below visible in full opacity. */}
            {isCurrentMonth && (
              <div
                style={{
                  position: "absolute",
                  left: `${monthFraction * 100}%`,
                  top: -5,
                  width: 2,
                  height: 32,
                  background: "var(--fg-1)",
                  borderRadius: 1,
                  transform: "translateX(-1px)",
                }}
              />
            )}
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "var(--bg-3)",
                borderRadius: 11,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${capturedPct}%`,
                  height: "100%",
                  background: fillBg,
                  borderRadius: 11,
                  transition: "width 0.5s",
                }}
              />
            </div>
            {/* Translucent overlay of the marker across the bar interior so
                the line is faintly visible through the colored fill / track,
                sitting under the caption (zIndex 2) so text isn't sliced. */}
            {isCurrentMonth && (
              <div
                style={{
                  position: "absolute",
                  left: `${monthFraction * 100}%`,
                  top: 0,
                  width: 2,
                  height: 22,
                  background: "rgba(0,0,0,0.15)",
                  borderRadius: 1,
                  transform: "translateX(-1px)",
                  zIndex: 1,
                }}
              />
            )}
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontFamily: "var(--font-mono)",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                zIndex: 2,
              }}
            >
              <span style={{ fontWeight: 700, color: fillColor }}>
                {capturedPct.toFixed(0)}% captured
              </span>
              <span style={{ color: "var(--fg-2)", marginLeft: 8 }}>
                · {row.sym}{fmt(Math.max(0, row.captured))} of {row.sym}{fmt(Math.max(0, row.needed))}
              </span>
            </div>
      </div>
      {/* Meta cluster: MoM / Pro / Plus / Status. Renders as 4 grid columns on
          desktop (via display: contents) and as a wrapping flex row on
          narrow screens (via the .mrr-region-meta media query). */}
      <div className="mrr-region-meta">
        <div className="mrr-meta-cell">
          <span
            style={{
              fontSize: 16,
              fontWeight: 700,
              fontFamily: "var(--font-mono)",
              color: row.momPct == null
                ? "var(--fg-3)"
                : row.momPct >= 0
                  ? "var(--color-jade)"
                  : "var(--fg-danger)",
            }}
          >
            {row.momPct == null
              ? "-"
              : `${row.momPct >= 0 ? "+" : ""}${row.momPct.toFixed(1)}%`}
          </span>
        </div>
        <div className="mrr-meta-cell">
          <SeatStat label="Pro" value={row.pro} color="var(--primary-5)" />
        </div>
        <div className="mrr-meta-cell">
          <SeatStat label="Plus" value={row.plus} color="var(--violet-5)" />
        </div>
        <div className="mrr-meta-cell">
          <span
            className="badge"
            style={{
              background: badgeBg,
              color: badgeFg,
              fontSize: 12,
              whiteSpace: "nowrap",
            }}
          >
            {paceText}
          </span>
        </div>
      </div>
    </div>
  );
}

